import React, { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/rae-enterprise/geofencing-consultation-1";
const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";
const JS_SRC = "https://assets.calendly.com/assets/external/widget.js";

let scriptLoadingPromise = null;

function ensureCalendlyLoaded() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CSS_HREF;
      document.head.appendChild(link);
    }
    if (document.querySelector(`script[src="${JS_SRC}"]`)) {
      // already loading
      const check = setInterval(() => {
        if (window.Calendly) {
          clearInterval(check);
          resolve(window.Calendly);
        }
      }, 50);
      return;
    }
    const s = document.createElement("script");
    s.src = JS_SRC;
    s.async = true;
    s.onload = () => resolve(window.Calendly);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return scriptLoadingPromise;
}

export function openCalendly() {
  ensureCalendlyLoaded().then((C) => {
    if (C && C.initPopupWidget) {
      C.initPopupWidget({ url: CALENDLY_URL });
    } else {
      // graceful fallback
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    }
  }).catch(() => {
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  });
}

// Pre-warm on mount so the popup is instant
export function useCalendlyPrewarm() {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    ensureCalendlyLoaded().catch(() => {});
  }, []);
}

export default function CalendlyButton({
  variant = "gold",
  children = "Book a Strategy Call",
  className = "",
  testId = "calendly-button",
  withIcon = true,
}) {
  useCalendlyPrewarm();
  const base = variant === "outline" ? "btn-outline-gold" : "btn-gold";
  return (
    <button
      type="button"
      onClick={openCalendly}
      className={`${base} btn-label ${className}`}
      data-testid={testId}
    >
      {children}
      {withIcon && <ArrowRight size={16} strokeWidth={1.6} />}
    </button>
  );
}
